import { uniqueId } from './utils/uniqueId';
/**
 * Safely parse a value to a float number, handling malformed number strings
 * @param {*} value - The value to parse
 * @param {number} defaultValue - Default value if parsing fails (default: 0)
 * @returns {number} Parsed number or default value
 */
const safeParseFloat = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') {
        return defaultValue;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) || !isFinite(parsed) ? defaultValue : parsed;
};

/**
 * Safely parse a value to an integer number, handling malformed number strings
 * @param {*} value - The value to parse
 * @param {number} defaultValue - Default value if parsing fails (default: 0)
 * @returns {number} Parsed integer or default value
 */
const safeParseInt = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') {
        return defaultValue;
    }
    const parsed = parseInt(value, 10);
    return isNaN(parsed) || !isFinite(parsed) ? defaultValue : parsed;
};

const dataLayerPush = (event, data) => {
    if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event, event_id: uniqueId(), ecommerce: { ...data } });

    }
};

export const dataLayerEvents = {
    addRefund: () => dataLayerPush('refund', gtagData.refundData()),
    addPurchase: () => dataLayerPush('purchase', gtagData.purchaseData()),
    viewItem: () => dataLayerPush('view_item',  gtagData.singleProductData()),
    viewCart: (data) => dataLayerPush('view_cart', gtagData.viewCartData(data)),
    beginCheckout: () => dataLayerPush('begin_checkout', gtagData.beginCheckoutData()),
    removeFromCart: product => dataLayerPush('remove_from_cart', gtagData.removeFromCartData(product)),
    addToWishList: (data) => dataLayerPush('add_to_wishlist', gtagData.wishListData(data)),
    viewItemList: (data, params) => dataLayerPush('view_item_list', gtagData.viewItemListData(data, params)),
    addShippingInfo: shippingType => dataLayerPush('add_shipping_info', gtagData.shippingInfoData(shippingType)),
    addPaymentInfo: (paymentType, couponCode) => dataLayerPush('add_payment_info', gtagData.paymentInfoData(paymentType, couponCode)),
    addToCart: (externalData, isSingle, selectedVariationId) =>  dataLayerPush('add_to_cart', fillAddToCartEvent(externalData, isSingle, selectedVariationId)),
    selectContent: data => dataLayerPush('select_content', data),
    selectPromotion: data => dataLayerPush('select_promotion', data),
    searchByKeyword: data => dataLayerPush('search', data),
    setUser: data => dataLayerPush('user', data),
};

export const gtagData = {
    singleProductData: (selectedVariationId) => {
        const product = window.mainProduct;
        const categories = product.categoriesNames && product.categoriesNames.map((value, index) => {
            return createCategoryObject(index, value);
        });

        const currentPrice = safeParseFloat(product.price.withoutTax.current);
        const formerPrice = safeParseFloat(product.price.withoutTax.former);
        const minQuantity = safeParseInt(product.quantity.min, 1);

        let data = {
            currency: 'EUR',
            value: currentPrice,
            items: [
                {
                    currency: 'EUR',
                    item_id: selectedVariationId || product.id,
                    item_name: product.name,
                    item_brand: product.vendor,
                    price: currentPrice * minQuantity,
                    quantity: minQuantity,
                    discount: formerPrice - currentPrice,
                    index: 0,
                    item_list_id: 'product_page',
                    item_list_name: 'Product page',
                    promotion_id: 'Product page',
                    promotion_name: 'Product page',
                    ...(product.specificationName && ({item_variant: `${product.specificationName} ${product.specificationValue}`})),
                }
            ]
        }

        if (categories.length) {
            categories.forEach(category => {
                data.items = data.items.map(item => {
                    return {...item, ...category};
                })
            })
        }

        return data;
    },

    cartProductData: (product, selectedVariationId) => {
        const categories = product.categoriesNames && product.categoriesNames.map((value, index) => {
            return createCategoryObject(index, value);
        });

        const currentPrice = safeParseFloat(product.price.withoutTax.current);
        const minQuantity = safeParseInt(product.quantity.min, 1);

        const data =  {
            currency: 'EUR',
            value: currentPrice,
            items: [
                {
                    currency: 'EUR',
                    item_id: selectedVariationId || product.prodlevid,
                    item_name: product.name,
                    item_brand: product.vendor,
                    price: currentPrice * minQuantity,
                    quantity: minQuantity,
                    item_list_id: 'meepakkers',
                    item_list_name: 'Meepakkers',
                    ...( product.specificationName && ({ item_variant: `${product.specificationName} ${product.specificationValue}` })),
                }
            ]
        }

        if (categories.length) {
            categories.forEach(category => {
                data.items = data.items.map(item => {
                    return {...item, ...category};
                })
            })
        }

        return data;
    },

    popupProductData: (product) => {
        const categories = product.categories && product.categories.map((value, index) => {
            return createCategoryObject(index, value);
        });

        let result = {
            currency: 'EUR',
            value: product.price,
            items: [
                {
                    currency: 'EUR',
                    item_id: product.id,
                    item_name: product.name,
                    item_brand: product.brand,
                    price: product.price,
                    quantity: product.quantity,
                    item_list_id: 'popup_offers_pdp',
                    item_list_name: 'Popup offers product',
                    ...(product.specification.name && ({ item_variant: `${product.specification.name} ${product.specification.value}`})),
                }
            ]
        }

        if (categories.length) {
            categories.forEach(category => {
                result = {...result, ...category};
            });
        }

        return result;
    },

    bundleProductsData: (productsIds, mainProductId) => {
        const areBundlesPresent = window.bundles.length;

        if (!areBundlesPresent) {
            return [];
        }

        let mainProduct = gtagData.singleProductData().items[0];

        if (mainProductId) {
            mainProduct = {
                ...mainProduct,
                item_id: mainProductId,
                item_list_id: 'bundles_products',
                item_list_name: 'Bundles',
            };
        }

        const bundles = window.bundles.map(item => {
            const sizes = item.product.variations;
            const selectedSize = sizes.find(size => productsIds.includes(size.id));

            if (sizes.length && selectedSize) {
                return selectedSize;
            }

            return item.product;
        });

        const items = bundles.map(item => {
            const currentPrice = safeParseFloat(item.price.withoutTax.current);
            const formerPrice = safeParseFloat(item.price.withoutTax.former);
            const discount = formerPrice > 0
                ? parseFloat((formerPrice - currentPrice).toFixed(2))
                : 0;

            if (productsIds.includes(item.productId || item.id)) {
                const quantity = safeParseInt(item.quantity, 0) || safeParseInt(item.quantity?.min, 1);

                return {
                    currency: 'EUR',
                    item_id: item.productId || item.id,
                    item_name: item.name,
                    item_brand: item.vendor,
                    price: currentPrice,
                    quantity: quantity,
                    item_list_id: 'bundles_products',
                    item_list_name: 'Bundles',
                    discount,
                }
            }

            return null;

        }).filter(item => item !== null);

        items.push(mainProduct);

        const totalSum = parseFloat(
            items.map(item => safeParseFloat(item?.price) * safeParseFloat(item?.quantity, 1))
                .reduce((a,b) => a + b, 0)
                .toFixed(2)
        );

        return {
            currency: 'EUR',
            value: totalSum,
            items,
        }
    },

    removeFromCartData: product => {
        const { quantity, prodlevid, getvendorname, getname } = product;

        return {
            currency: 'EUR',
            items: [{
              item_id: prodlevid,
              item_name: getname,
              item_brand: getvendorname,
              price: safeParseFloat(product?.gtag?.productPrice),
              currency: 'EUR',
              quantity,
            }],
        }
    },

    beginCheckoutData: () => {
        return {
            currency: 'EUR',
            value: totalOrderValue,
            items: fillCartProductsData({
                // item_list_id: 'meepakkers',
                // item_list_name: 'Meepakkers',
            }),
        }
    },

    shippingInfoData: (shipping_tier, coupon) => {
        return {
            coupon,
            shipping_tier,
            currency: 'EUR',
            value: totalOrderValue,
            items: fillCartProductsData({
                // item_list_id: 'meepakkers',
                // item_list_name: 'Meepakkers',
            }),
        }
    },

    paymentInfoData: (payment_type, coupon) => {
        return {
            coupon,
            payment_type,
            currency: 'EUR',
            value: totalOrderValue,
            items: fillCartProductsData({
                // item_list_id: 'meepakkers',
                // item_list_name: 'Meepakkers',
            }),
        }
    },

    viewItemListData: (data, params) => {
        const items = data?.map(item => {
            const categories = item?.categoriesNames && item?.categoriesNames?.map((value, index) => {
                return createCategoryObject(index, value);
            });

            let result =  {
                item_id: item?.prodlevid,
                item_name: item?.name,
                item_brand: item?.vendor,
                price: safeParseFloat(item?.price?.withoutTax?.current),
                currency: 'EUR',
                quantity: item?.quantity?.min,
            }

            if (categories.length) {
                categories.forEach(category => {
                    result = {...result, ...category};
                });
            }

            return result;
        })

        return {
            items,
            item_list_name: params?.listName,
            item_list_id: params?.listId,
        }
    },

    wishListData: (product) => {
        if (window.mainProduct) {
            return gtagData.singleProductData();
        }

        const currentPrice = safeParseFloat(product.price.current);

        return {
            currency: 'EUR',
            value: currentPrice,
            items: [{
                item_id: product.prodlevid,
                item_name: product.name,
                item_brand: product.vendor,
                price: currentPrice,
                currency: 'EUR',
                quantity: product.quantity.min,
                index: 0,
                item_list_id: 'wishlist',
                item_list_name: 'Wishlist',
                item_variant: `${product.specificationName} ${product.specificationValue}`,
            }],
        }
    },

    purchaseData: () => {
        const values = $('.js_thankyou-container').data('info');

        return {
            transaction_id: values?.transaction_id,
            value: safeParseFloat(values?.value),
            tax: safeParseFloat(values?.tax),
            shipping: safeParseFloat(values?.shipping),
            currency: 'EUR',
            ...(values?.coupon && {coupon: values?.coupon}),
            items: values?.items?.map((item) => {
                const categories =  item?.categoriesNames && item?.categoriesNames?.map((value, index) => {
                    return createCategoryObject(index, value);
                }) || [];

                let result = {...item};

                if (categories.length) {
                    categories.forEach(category => {
                        result = {...result, ...category};
                    });
                }

                delete result.categoriesNames;

                return result;
            }),
            isNewCustomer: values?.isNewCustomer,
        }
    },

    refundData: () => {
        const values = $('.js_return-container').data('info');

        values?.items?.forEach(item => {
            if (item?.categoriesNames) {
                delete item.categoriesNames;
            }
        });

        return values || {};
    },

    viewCartData: data => {
        const coupon = $('.js_coupon').data('coupon');
        const totalPrice = $('.js_cart-lines').data('total');

        return {
            currency: 'EUR',
            value: totalPrice,
            items: data?.map((item, index) => {
                const categories = item?.categories && item?.categories?.map((value, index) => {
                    return createCategoryObject(index, value);
                }) || [];

                let result = {
                    item_id: item?.prodlevid,
                    item_name: item?.getname,
                    discount: safeParseFloat(item?.discount),
                    index: index,
                    item_brand: item?.getvendorname,
                    item_list_id: item?.extraData?.pageSource,
                    item_list_name: item?.extraData?.pageSource,
                    price: item?.productPrice,
                    quantity: item?.quantity,
                    coupon: coupon,
                    ...( item?.specification?.name && ({ item_variant: `${item?.specification?.name} ${item?.specification?.value}` })),
                }

                if (categories.length) {
                    categories.forEach(category => {
                        result = {...result, ...category};
                    });
                }

                return result;
            })
        };
    }
};

const fillAddToCartEvent = (externalData, isBundle, selectedVariationId) => {
    if (selectedVariationId === 'popup-data') {
        return gtagData.popupProductData(externalData);
    }

    if (typeof isBundle === 'string' || !isBundle) {
        return isBundle
            ? gtagData.cartProductData(externalData, selectedVariationId)
            : gtagData.singleProductData(selectedVariationId);
    }

    return gtagData.bundleProductsData(externalData, selectedVariationId);
}

const fillCartProductsData = () => {
    return window?.cartProducts?.map((item, index) => {
        const categories = item?.categories && Object.values(item?.categories)?.map((value, index) => {
            return createCategoryObject(index, value);
        });
        const specification = item?.specification?.name;
        const coupon = item?.coupon;

        let result = {
            item_id: item?.id,
            item_name: item?.name,
            price: safeParseFloat(item?.price),
            quantity: safeParseInt(item?.quantity, 1),
            currency: 'EUR',
            index: index,
            item_list_name: item?.extraData?.pageSource,
            item_list_id: item?.extraData?.pageSource,
            item_brand: item?.brand,
            discount: safeParseFloat(item?.discount),
            ...(coupon && coupon?.code && { coupon: coupon?.code, discount: safeParseFloat(coupon?.discount) }),
            ...(specification && {item_variant: `${item?.specification?.name} ${item?.specification?.value}`}),
            promotion_id: item?.extraData?.pageSource,
            promotion_name: item?.extraData?.pageSource,
        }

        if (categories.length) {
            categories.forEach(category => {
                result = {...result, ...category};
            });
        }

        return result;
    });
}

const totalOrderValue = safeParseFloat(window?.googleAnalyticCart?.price?.rawPriceWithoutTax);

const createCategoryObject = (index, value) => !index ? {item_category: value} : {[`item_category${index + 1}`]: value};
